import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import api from '../api/axios';
import { MessageSquare, TrendingUp, Clock, Star, ArrowRight, Settings, Trash2, HelpCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import Stepper, { Step } from '../components/Stepper';

// Project limit translations in 5 languages
const projectLimitTranslations = {
  tr: {
    projectsUsed: "proje kullanıldı",
    maxProjectsReached: "Maksimum proje sayısı ({{max}}) ulaşıldı. {{plan}} planınız için lütfen yükseltin.",
    canAddMoreProjects: "Yeni bir proje oluşturun. {{remaining}} daha proje ekleyebilirsiniz.",
    upgradePlanWarning: "⚠️ {{plan}} planınız için maksimum proje limitine ({{max}}) ulaştınız. Daha fazla proje oluşturmak için lütfen yükseltin.",
    createProject: "Proje Oluştur",
    cancel: "İptal",
    deleteProject: "Sil",
    deleteProjectConfirm: "Bu projeyi silmek istediğinizden emin misiniz?",
    deleteProjectWarning: "Bu işlem geri alınamaz ve tüm proje verileri silinecektir.",
    projectDeletedSuccess: "Proje başarıyla silindi",
    projectDeleteFailed: "Proje silemedi",
  },
  en: {
    projectsUsed: "projects used",
    maxProjectsReached: "Maximum projects ({{max}}) reached for your {{plan}} plan. Please upgrade.",
    canAddMoreProjects: "Create a new project. You can add {{remaining}} more project(s).",
    upgradePlanWarning: "⚠️ You have reached the maximum project limit ({{max}}) for your {{plan}} plan. Please upgrade to create more projects.",
    createProject: "Create Project",
    cancel: "Cancel",
    deleteProject: "Delete",
    deleteProjectConfirm: "Are you sure you want to delete this project?",
    deleteProjectWarning: "This action cannot be undone. All project data will be deleted.",
    projectDeletedSuccess: "Project deleted successfully",
    projectDeleteFailed: "Failed to delete project",
  },
  de: {
    projectsUsed: "Projekte verwendet",
    maxProjectsReached: "Maximale Anzahl von Projekten ({{max}}) erreicht. Bitte führen Sie ein Upgrade für Ihren {{plan}}-Plan durch.",
    canAddMoreProjects: "Erstellen Sie ein neues Projekt. Sie können {{remaining}} weitere Projekt(e) hinzufügen.",
    upgradePlanWarning: "⚠️ Sie haben die maximale Projektgrenze ({{max}}) für Ihren {{plan}}-Plan erreicht. Bitte führen Sie ein Upgrade durch, um mehr Projekte zu erstellen.",
    createProject: "Projekt erstellen",
    cancel: "Abbrechen",
    deleteProject: "Löschen",
    deleteProjectConfirm: "Möchten Sie dieses Projekt wirklich löschen?",
    deleteProjectWarning: "Diese Aktion kann nicht rückgängig gemacht werden. Alle Projektdaten werden gelöscht.",
    projectDeletedSuccess: "Projekt erfolgreich gelöscht",
    projectDeleteFailed: "Fehler beim Löschen des Projekts",
  },
  es: {
    projectsUsed: "proyectos utilizados",
    maxProjectsReached: "Se alcanzó el máximo de proyectos ({{max}}). Por favor, actualice su plan {{plan}}.",
    canAddMoreProjects: "Cree un nuevo proyecto. Puede agregar {{remaining}} proyecto(s) más.",
    upgradePlanWarning: "⚠️ Ha alcanzado el límite máximo de proyectos ({{max}}) para su plan {{plan}}. Por favor, actualice para crear más proyectos.",
    createProject: "Crear proyecto",
    cancel: "Cancelar",
    deleteProject: "Eliminar",
    deleteProjectConfirm: "¿Está seguro de que desea eliminar este proyecto?",
    deleteProjectWarning: "Esta acción no se puede deshacer. Se eliminarán todos los datos del proyecto.",
    projectDeletedSuccess: "Proyecto eliminado exitosamente",
    projectDeleteFailed: "Error al eliminar el proyecto",
  },
  fr: {
    projectsUsed: "projets utilisés",
    maxProjectsReached: "Nombre maximum de projets ({{max}}) atteint. Veuillez mettre à niveau votre plan {{plan}}.",
    canAddMoreProjects: "Créez un nouveau projet. Vous pouvez ajouter {{remaining}} projet(s) supplémentaire(s).",
    upgradePlanWarning: "⚠️ Vous avez atteint la limite maximale de projets ({{max}}) pour votre plan {{plan}}. Veuillez mettre à niveau pour créer plus de projets.",
    createProject: "Créer un projet",
    cancel: "Annuler",
    deleteProject: "Supprimer",
    deleteProjectConfirm: "Êtes-vous sûr de vouloir supprimer ce projet?",
    deleteProjectWarning: "Cette action ne peut pas être annulée. Toutes les données du projet seront supprimées.",
    projectDeletedSuccess: "Projet supprimé avec succès",
    projectDeleteFailed: "Impossible de supprimer le projet",
  },
};

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { projects, activeProject, createProject, switchProject, loading: authLoading, planInfo, loadProjects } = useAuth();
  const { language, t } = useLanguage();
  const pt = projectLimitTranslations[language] || projectLimitTranslations.en;
  const safeProjects = Array.isArray(projects) ? projects : [];
  const maxProjects = planInfo?.max_projects ?? Number.POSITIVE_INFINITY;
  const maxProjectsLabel = Number.isFinite(maxProjects) ? maxProjects : '∞';
  const reachedProjectLimit =
    !!planInfo && Number.isFinite(maxProjects) && planInfo.current_projects >= maxProjects;

  // Helper function to interpolate translations
  const interpolate = (template, values) => {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => values[key] || match);
  };

  // Helper function to translate error messages from backend
  const translateErrorMessage = (errorDetail) => {
    if (!errorDetail) return pt.projectCreateFailed;

    // Handle "Maximum projects" error
    if (errorDetail.includes("Maximum projects")) {
      const match = errorDetail.match(/Maximum projects \((\d+)\).*?\b(\w+)\b plan/);
      if (match) {
        const maxProjects = match[1];
        const planType = match[2];
        return interpolate(pt.maxProjectsReached, {
          max: maxProjects,
          plan: planType
        });
      }
    }

    // Handle "Maximum active projects" error
    if (errorDetail.includes("Maximum active projects")) {
      return "Maximum active projects reached. Please deactivate another project first.";
    }

    // Handle "Maximum domains" error
    if (errorDetail.includes("Maximum domains")) {
      return "Maximum domains reached for your account plan.";
    }

    // Default: show backend error
    return errorDetail;
  };
  const [showFirstProjectModal, setShowFirstProjectModal] = useState(false);
  const [firstProjectName, setFirstProjectName] = useState('');
  const [firstProjectDomain, setFirstProjectDomain] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectSettings, setProjectSettings] = useState({ comments_enabled: true });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDomain, setNewProjectDomain] = useState('');
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);

  useEffect(() => {
    if (activeProject) {
      fetchStats();
      fetchProjectSettings();
    }
  }, [activeProject]);

  useEffect(() => {
    // Show modal if user has no projects (mandatory for new users)
    if (projects.length === 0 && !authLoading) {
      setShowFirstProjectModal(true);
    }
  }, [projects, authLoading]);

  const fetchStats = async () => {
    if (!activeProject) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/dashboard/stats', {
        params: {
          project_id: activeProject.id
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error(t.statsLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectSettings = async () => {
    if (!activeProject) return;
    setLoadingSettings(true);
    try {
      const response = await api.get(`/projects/${activeProject.id}/settings`);
      setProjectSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch project settings:', error);
      // Default to enabled if fetch fails
      setProjectSettings({ comments_enabled: true });
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleToggleComments = async (enabled) => {
    if (!activeProject) return;
    setLoadingSettings(true);
    try {
      const response = await api.put(`/projects/${activeProject.id}/settings`, {
        comments_enabled: enabled
      });
      setProjectSettings(response.data);
      toast.success(enabled ? (t.commentsEnabled || 'Yorumlar açıldı') : (t.commentsDisabled || 'Yorumlar kapatıldı'));
    } catch (error) {
      console.error('Failed to update project settings:', error);
      toast.error(t.settingsUpdateFailed || 'Ayarlar güncellenemedi');
      // Revert on error
      setProjectSettings(prev => ({ ...prev, comments_enabled: !enabled }));
    } finally {
      setLoadingSettings(false);
    }
  };

  const validateDomain = (domain) => {
    if (!domain.trim()) return true; // Optional
    const regex = /^[\w\-\.]+\.\w{2,}$|^\*\.[\w\-\.]+\.\w{2,}$/;
    return regex.test(domain.toLowerCase());
  };

  const handleDeleteProject = async (projectId) => {
    setDeletingProject(true);
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success(pt.projectDeletedSuccess);
      setShowDeleteConfirm(false);
      setProjectToDelete(null);
      // Refresh projects list
      await loadProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error(translateErrorMessage(error.response?.data?.detail) || pt.projectDeleteFailed);
    } finally {
      setDeletingProject(false);
    }
  };

  const handleCreateFirstProject = async (e) => {
    e.preventDefault();
    if (!firstProjectName.trim()) {
      toast.error(t.pleaseEnterProjectName);
      return;
    }

    if (!validateDomain(firstProjectDomain)) {
      toast.error('Invalid domain format. Example: example.com or *.example.com');
      return;
    }

    setCreatingProject(true);
    try {
      await createProject(firstProjectName.trim(), firstProjectDomain.toLowerCase());
      toast.success(t.projectCreatedWelcome);
      setShowFirstProjectModal(false);
      setFirstProjectName('');
      setFirstProjectDomain('');
      // Stats will be fetched automatically when activeProject is set
    } catch (error) {
      toast.error(translateErrorMessage(error.response?.data?.detail) || t.projectCreateFailed);
    } finally {
      setCreatingProject(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Feedback Dashboard - FeedPanels</title>
        <meta name="description" content="Your FeedPanels feedback dashboard. See all customer feedback, feature requests, and insights in one place." />
        <link rel="canonical" href="https://feedpanels.com/dashboard" />
      </Helmet>
      <>
      {/* First Project Modal - Mandatory for new users */}
      <Dialog 
        open={showFirstProjectModal} 
        onOpenChange={(open) => {
          // Prevent closing if no projects exist (mandatory modal)
          if (!open && projects.length === 0) {
            return;
          }
          setShowFirstProjectModal(open);
        }}
      >
        <DialogContent
          className="w-[95vw] sm:w-full sm:max-w-md [&>button]:hidden p-3 sm:p-6"
          onInteractOutside={(e) => {
            // Prevent closing by clicking outside if no projects exist
            if (projects.length === 0) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            // Prevent closing with ESC key if no projects exist
            if (projects.length === 0) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl">{t.welcomeToFeedbackFlow}</DialogTitle>
            <DialogDescription className="text-sm sm:text-base mt-2">
              {t.firstProjectModalDescription}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateFirstProject} className="space-y-3 sm:space-y-4 mt-4">
            <div>
              <Label htmlFor="firstProjectName" className="text-xs sm:text-sm font-medium">
                {t.projectName}
              </Label>
              <Input
                id="firstProjectName"
                value={firstProjectName}
                onChange={(e) => setFirstProjectName(e.target.value)}
                placeholder={t.projectNamePlaceholder}
                required
                disabled={creatingProject}
                className="mt-2 text-xs sm:text-sm"
                data-testid="first-project-name-input"
                autoFocus
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {t.moreProjectsHint}
              </p>
            </div>
            <div>
              <Label htmlFor="firstProjectDomain" className="text-xs sm:text-sm font-medium">
                Domain (Optional)
              </Label>
              <Input
                id="firstProjectDomain"
                value={firstProjectDomain}
                onChange={(e) => setFirstProjectDomain(e.target.value)}
                placeholder="example.com or *.example.com"
                disabled={creatingProject}
                className="mt-2 text-xs sm:text-sm"
                data-testid="first-project-domain-input"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                You can add or change domains later
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="flex-1"
                disabled={creatingProject || !firstProjectName.trim()}
                data-testid="create-first-project-submit"
              >
                {creatingProject ? t.creatingProject : t.createProject}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <div className="p-3 sm:p-6 md:p-8">
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2" data-testid="dashboard-title">
                {t.welcomeBack}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{t.dashboardIntro}</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 md:gap-4 w-full sm:w-auto">
              <Button
                size="sm"
                onClick={() => navigate('/dashboard/project-settings?tab=pricing')}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white w-full sm:w-auto"
              >
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="text-xs sm:text-sm">{t.upgrade || 'Upgrade'}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowIntegrationModal(true)}
                className="flex items-center gap-2 w-full sm:w-auto"
                title={t.howToIntegrateButton}
              >
                <HelpCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="hidden sm:inline text-xs sm:text-sm">{t.howToIntegrateButton}</span>
                <span className="sm:hidden text-xs">{t.integrationGuideTitle || 'Entegrasyon'}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Integration Guide Modal - Stepper (motion) */}
        <Dialog open={showIntegrationModal} onOpenChange={setShowIntegrationModal}>
          <DialogContent className="w-[95vw] sm:w-full sm:max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden flex flex-col p-3 sm:p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl text-gray-900 dark:text-white">
                <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" />
                {t.integrationGuideTitle}
              </DialogTitle>
            </DialogHeader>

            {showIntegrationModal && (
              <div className="integration-guide-stepper-wrapper flex-1 min-h-0 overflow-hidden flex flex-col">
                <Stepper
                  key="integration-guide-stepper"
                  initialStep={1}
                  onFinalStepCompleted={() => setShowIntegrationModal(false)}
                  backButtonText={t.previous}
                  nextButtonText={t.next}
                  completeButtonText={t.close}
                  stepCircleContainerClassName="!border-gray-200 dark:!border-gray-700 !shadow-none"
                >
                <Step>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t.integrationPreStep}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {t.integrationPreStepDesc}
                  </p>
                </Step>
                <Step>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t.integrationStep1}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {t.integrationStep1Desc}
                  </p>
                </Step>
                <Step>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t.integrationStep2}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {t.integrationStep2Desc}
                  </p>
                </Step>
                <Step>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t.integrationStep3}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {t.integrationStep3Desc}
                  </p>
                </Step>
                <Step>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t.integrationStep4}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {t.integrationStep4Desc}
                  </p>
                </Step>
                <Step>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t.integrationStep5}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                    {t.integrationStep5Desc}
                  </p>
                </Step>
              </Stepper>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* IP License Warning Banner Removed */}

        {/* Stats / Analytics Cards (hide on Free plan) */}
        {planInfo?.plan_type !== 'free' && (
          <>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card data-testid="stat-total-feedbacks">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center justify-between">
                      {t.totalFeedback}
                      <MessageSquare className="w-4 h-4 text-cyan-600" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.total_feedbacks || 0}</div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.allTime}</p>
                  </CardContent>
                </Card>

                <Card data-testid="stat-active-feedbacks">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center justify-between">
                      {t.activeFeedback}
                      <TrendingUp className="w-4 h-4 text-orange-500" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.active_feedbacks || 0}</div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.inProgress}</p>
                  </CardContent>
                </Card>

                <Card data-testid="stat-this-month">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center justify-between">
                      {t.thisMonth}
                      <Clock className="w-4 h-4 text-purple-600" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.this_month_feedbacks || 0}</div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.newSubmissions}</p>
                  </CardContent>
                </Card>

                <Card data-testid="stat-top-feedback">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center justify-between">
                      {t.topFeedback}
                      <Star className="w-4 h-4 text-yellow-500" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                      {stats?.top_feedback?.upvotes || 0}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {stats?.top_feedback?.title || t.noFeedbackYet}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {/* All Projects */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t.yourProjects}</h2>
              {planInfo && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {planInfo.current_projects} / {maxProjectsLabel} {pt.projectsUsed} ({planInfo.plan_type} plan)
                </p>
              )}
            </div>
            <Button
              onClick={() => {
                if (reachedProjectLimit) {
                  toast.error(interpolate(pt.maxProjectsReached, {
                    max: maxProjectsLabel,
                    plan: planInfo.plan_type
                  }));
                  return;
                }
                setShowCreateProjectModal(true);
              }}
              className={reachedProjectLimit ? 'opacity-50 cursor-not-allowed' : ''}
            >
              + {pt.createProject}
            </Button>
          </div>

          {/* Create Project Modal */}
          <Dialog open={showCreateProjectModal} onOpenChange={setShowCreateProjectModal}>
            <DialogContent className="w-[95vw] sm:w-full sm:max-w-md p-3 sm:p-6">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl">{pt.createProject}</DialogTitle>
                <DialogDescription className="text-xs sm:text-sm">
                  {reachedProjectLimit ? (
                    <span className="text-red-600 font-semibold">
                      {interpolate(pt.upgradePlanWarning, {
                        max: maxProjectsLabel,
                        plan: planInfo.plan_type
                      })}
                    </span>
                  ) : (
                    interpolate(pt.canAddMoreProjects, {
                      remaining: Number.isFinite(maxProjects) ? (maxProjects - (planInfo?.current_projects || 0)) : '∞'
                    })
                  )}
                </DialogDescription>
              </DialogHeader>
              {planInfo && (!Number.isFinite(maxProjects) || planInfo.current_projects < maxProjects) && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newProjectName.trim()) {
                      toast.error(t.pleaseEnterProjectName);
                      return;
                    }
                    if (!validateDomain(newProjectDomain)) {
                      toast.error('Invalid domain format. Example: example.com or *.example.com');
                      return;
                    }
                    setCreatingProject(true);
                    try {
                      await createProject(newProjectName.trim(), newProjectDomain.toLowerCase());
                      toast.success(t.projectCreated || 'Project created successfully!');
                      setShowCreateProjectModal(false);
                      setNewProjectName('');
                      setNewProjectDomain('');
                    } catch (error) {
                      toast.error(translateErrorMessage(error.response?.data?.detail) || t.projectCreateFailed);
                    } finally {
                      setCreatingProject(false);
                    }
                  }}
                  className="space-y-3 sm:space-y-4 mt-4"
                >
                  <div>
                    <Label htmlFor="newProjectName" className="text-xs sm:text-sm font-medium">
                      {t.projectName}
                    </Label>
                    <Input
                      id="newProjectName"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      placeholder={t.projectNamePlaceholder}
                      required
                      disabled={creatingProject}
                      className="mt-2 text-xs sm:text-sm"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Label htmlFor="newProjectDomain" className="text-xs sm:text-sm font-medium">
                      Domain (Optional)
                    </Label>
                    <Input
                      id="newProjectDomain"
                      value={newProjectDomain}
                      onChange={(e) => setNewProjectDomain(e.target.value)}
                      placeholder="example.com or *.example.com"
                      disabled={creatingProject}
                      className="mt-2 text-xs sm:text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      You can add or change domains later
                    </p>
                  </div>
                  <div className="flex gap-2 sm:gap-3 pt-2">
                    <Button type="submit" className="flex-1 text-xs sm:text-sm" disabled={creatingProject || !newProjectName.trim()}>
                      {creatingProject ? t.creatingProject : pt.createProject}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 text-xs sm:text-sm"
                      onClick={() => setShowCreateProjectModal(false)}
                    >
                      {pt.cancel}
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* Delete Project Confirmation Dialog */}
          <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-red-600">{pt.deleteProjectConfirm}</DialogTitle>
                <DialogDescription className="text-red-500 font-semibold mt-2">
                  {pt.deleteProjectWarning}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-4">
                {projectToDelete && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t.projectName}: <span className="font-semibold">{projectToDelete.name}</span>
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setProjectToDelete(null);
                  }}
                  disabled={deletingProject}
                >
                  {pt.cancel}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleDeleteProject(projectToDelete?.id)}
                  disabled={deletingProject}
                >
                  {deletingProject ? t.loading : pt.deleteProject}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {safeProjects.map((project) => (
              <Card
                key={project.id}
                className={`transition-all hover:shadow-md ${
                  activeProject?.id === project.id ? 'ring-2 ring-cyan-600' : ''
                }`}
                data-testid={`project-card-${project.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 cursor-pointer" onClick={() => switchProject(project.id)}>
                      <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{project.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{project.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {activeProject?.id === project.id && (
                        <span className="px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 text-xs font-semibold rounded whitespace-nowrap">
                          {t.active}
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setProjectToDelete(project);
                          setShowDeleteConfirm(true);
                        }}
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title={pt.deleteProject}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer" onClick={() => switchProject(project.id)}>
                    <p>{t.created}: {new Date(project.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : language === 'de' ? 'de-DE' : language === 'es' ? 'es-ES' : language === 'fr' ? 'fr-FR' : 'en-US')}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Project Settings */}
        {activeProject && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>{t.projectSettings || 'Proje Ayarları'}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="comments-toggle" className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t.enableComments || 'Yorumları Etkinleştir'}
                  </Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t.enableCommentsDescription || 'Kullanıcıların feedback\'lere yorum yapmasına izin ver'}
                  </p>
                </div>
                <Switch
                  id="comments-toggle"
                  checked={projectSettings.comments_enabled}
                  onCheckedChange={handleToggleComments}
                  disabled={loadingSettings}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t.quickActions}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/dashboard/feedback" data-testid="quick-action-view-feedback">
                <Button variant="outline" className="w-full justify-between">
                  <span>{t.viewAllFeedback}</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/dashboard/roadmap" data-testid="quick-action-manage-roadmap">
                <Button variant="outline" className="w-full justify-between">
                  <span>{t.manageRoadmap}</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/dashboard/widget" data-testid="quick-action-setup-widget">
                <Button variant="outline" className="w-full justify-between">
                  <span>{t.setupWidget}</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* IP License Status Card Removed */}

          <Card>
            <CardHeader>
              <CardTitle>{t.publicPages}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {activeProject ? (
                <>
                  <a 
                    href={`/feedback/${activeProject.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    data-testid="public-feedback-link"
                  >
                    <Button variant="outline" className="w-full justify-between">
                      <span>{t.publicFeedback}</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                  <a 
                    href={`/roadmap/${activeProject.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    data-testid="public-roadmap-link"
                  >
                    <Button variant="outline" className="w-full justify-between">
                      <span>{t.publicRoadmap}</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                  <a 
                    href={`/changelog/${activeProject.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    data-testid="public-changelog-link"
                  >
                    <Button variant="outline" className="w-full justify-between">
                      <span>{t.publicChangelog}</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  {t.selectProjectToViewPublicPages}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
    </>
  );
};